import { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translateBatch } from '../services/translationService';

/**
 * Hook to translate static UI strings using Google Translation API.
 * Pass an object of { key: "English text" } and get back translated versions.
 * Returns English immediately, translates async when language != 'en'.
 *
 * Usage:
 *   const UI_STRINGS = { title: "Impact Analyser", describe: "Describe Yourself" };
 *   const ui = useTranslatedUI(UI_STRINGS);
 *   // ui.title => "ప్రభావ విశ్లేషకుడు" (when Telugu selected)
 *
 * IMPORTANT: Pass a STABLE reference (const outside component or useMemo).
 */
export function useTranslatedUI(strings) {
  const { language } = useLanguage();
  const [translated, setTranslated] = useState(strings);
  const lastLangRef = useRef('en');

  useEffect(() => {
    if (language === 'en') {
      setTranslated(strings);
      lastLangRef.current = 'en';
      return;
    }

    if (lastLangRef.current === language) return;

    let cancelled = false;
    const keys = Object.keys(strings);
    const values = keys.map(k => strings[k]);

    translateBatch(values, language).then(results => {
      if (cancelled) return;
      const obj = {};
      keys.forEach((k, i) => {
        obj[k] = results[i] || strings[k];
      });
      setTranslated(obj);
      lastLangRef.current = language;
    });

    return () => { cancelled = true; };
  }, [language, strings]);

  return translated;
}
